/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { generatePatternStyles } from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import compactListMarkup from '../../../utils/compactListMarkup';
import getListMarkerBaselineOffset from './getListMarkerBaselineOffset';
import {
  getListMarkerGlyph,
  getListMarkerShape,
  getNormalizedTextListStyle,
  getOrderedListMarkerColumnWidth,
  hasSemanticList,
} from './listStyle';
import getListMarkerColor from './getListMarkerColor';
import { generateFontFamily } from './generateFontFamily';
import measureTextWidth from './measureTextWidth';

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function styleAttribute(styles) {
  return Object.entries(styles)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
    .map(([property, value]) => `${property}:${value}`)
    .join(';');
}

const isHtmlElementNode = (node) => node.nodeType === 1;

function wrapDirectListItemContentInParagraphs(content) {
  const { body } = new DOMParser().parseFromString(content, 'text/html');

  for (const listItem of Array.from(body.querySelectorAll('li'))) {
    let paragraph = null;

    for (const child of Array.from(listItem.childNodes)) {
      if (
        isHtmlElementNode(child) &&
        (child.tagName === 'P' ||
          child.tagName === 'UL' ||
          child.tagName === 'OL')
      ) {
        paragraph = null;
        continue;
      }

      if (!paragraph) {
        paragraph = listItem.ownerDocument.createElement('p');
        listItem.insertBefore(paragraph, child);
      }

      paragraph.appendChild(child);
    }
  }

  return body.innerHTML;
}

/**
 * Adds visual markers and inline layout to normalized semantic list markup.
 * The persisted content remains semantic and marker-free; decorative markers
 * are hidden from assistive technology to avoid duplicate announcements.
 */
export function getListRenderContent({
  content,
  color,
  listStyle,
  font,
  fontMetrics,
  fontSize,
  fontStyle,
  fontWeight,
  lineHeight = 1,
  dataToStyleX = (value) => `${value}px`,
  dataToStyleY = (value) => `${value}px`,
  dataToFontSizeY = dataToStyleY,
  dataToMarkerStyleX = dataToStyleX,
  dataToMarkerStyleY = dataToStyleY,
  dataToOrderedMarkerStyleX = dataToMarkerStyleX,
  hideMarkers = false,
  paragraphStyle,
  paragraphTextStyle,
}) {
  if (!hasSemanticList(content)) {
    return content;
  }

  const normalized = getNormalizedTextListStyle(listStyle, fontSize);
  const markerSize = normalized.marker.size || fontSize;
  const markerShape = getListMarkerShape(normalized, fontSize, lineHeight);
  const glyphBaselineOffset = dataToMarkerStyleY(
    getListMarkerBaselineOffset({
      fontMetrics,
      fontSize,
      lineHeight,
      markerSize,
      markerBaselineShift: normalized.marker.baselineShift,
    }),
    markerSize
  );
  const markerColor = hideMarkers
    ? 'transparent'
    : generatePatternStyles(
        getListMarkerColor({ content, color, listStyle: normalized }),
        'color'
      ).color;
  const listStack = [];
  const compactedContent = compactListMarkup(content);
  const renderContent =
    paragraphStyle || paragraphTextStyle
      ? wrapDirectListItemContentInParagraphs(compactedContent)
      : compactedContent;
  const { body: measurementRoot } = new DOMParser().parseFromString(
    renderContent,
    'text/html'
  );
  const orderedMarkerColumns = Array.from(
    measurementRoot.querySelectorAll('ol')
  ).map((list) => {
    const itemCount = Array.from(list.children).filter(
      (child) => child.tagName === 'LI'
    ).length;
    return getOrderedListMarkerColumnWidth(
      itemCount,
      (glyph) =>
        measureTextWidth(glyph, {
          fallbackWidth: getMarkerFallbackWidth(glyph, markerSize),
          fontFamily: generateFontFamily(font),
          fontSize: markerSize,
          fontStyle,
          fontWeight,
        }),
      markerSize
    );
  });
  let orderedListIndex = 0;

  return renderContent.replace(
    /<(\/)?(ul|ol|li|p)(?:\s[^>]*)?>/gi,
    (token, closing, rawTag) => {
      const tag = rawTag.toLowerCase();

      if (closing) {
        if (tag === 'p') {
          return `${paragraphTextStyle ? '</span>' : ''}${
            paragraphStyle ? '</span>' : ''
          }</p>`;
        }
        if (tag === 'li') {
          return '</div></li>';
        }
        if (tag === 'ul' || tag === 'ol') {
          listStack.pop();
        }
        return `</${tag}>`;
      }

      if (tag === 'ul' || tag === 'ol') {
        const depth = listStack.length;
        listStack.push({
          type: tag === 'ol' ? 'ordered' : 'unordered',
          count: 0,
          markerColumn:
            tag === 'ol'
              ? orderedMarkerColumns[orderedListIndex++] || markerSize
              : markerSize,
        });
        const padding = dataToStyleX(
          depth === 0 ? normalized.indent : normalized.nestedIndent
        );
        return `<${tag} role="list" data-list-depth="${depth}" style="${styleAttribute(
          {
            'list-style': 'none',
            margin: '0',
            'margin-block-start':
              depth === 0 ? undefined : dataToStyleY(normalized.itemSpacing),
            padding: '0',
            'padding-inline-start': padding,
          }
        )}">`;
      }

      if (tag === 'p') {
        return [
          '<p style="font:inherit;margin:0;padding:0">',
          paragraphStyle
            ? `<span class="text-list-inline-content" style="${styleAttribute(
                paragraphStyle
              )}">`
            : '',
          paragraphTextStyle
            ? `<span class="text-list-inline-text" style="${styleAttribute(
                paragraphTextStyle
              )}">`
            : '',
        ].join('');
      }

      const currentList = listStack[listStack.length - 1] || {
        type: 'unordered',
        count: 0,
        markerColumn: markerSize,
      };
      const isFirstItem = currentList.count === 0;
      currentList.count += 1;
      const marker = getListMarkerGlyph(
        normalized,
        currentList.type,
        currentList.count
      );
      const markerColumn = dataToOrderedMarkerStyleX(
        currentList.markerColumn,
        markerSize
      );
      const markerColumnLayout = dataToOrderedMarkerStyleX(
        currentList.markerColumn,
        fontSize
      );
      const markerGap = dataToStyleX(normalized.markerGap);
      const isOrdered = currentList.type === 'ordered';
      const currentMarkerShape = isOrdered ? null : markerShape;
      const markerHorizontalShift =
        !isOrdered && normalized.marker.preset === 'custom'
          ? normalized.marker.horizontalShift || 0
          : 0;
      const liStyles = {
        margin: '0',
        'margin-block-start': isFirstItem
          ? '0'
          : dataToStyleY(normalized.itemSpacing),
        padding: '0',
        'padding-inline-start': isOrdered
          ? `calc(${markerColumnLayout} + ${markerGap})`
          : '0',
        position: 'relative',
        ...(isOrdered
          ? null
          : {
              'align-items': 'start',
              'column-gap': markerGap,
              display: 'grid',
              'grid-template-columns': 'max-content minmax(0,1fr)',
            }),
      };
      const markerStyles = {
        background: currentMarkerShape
          ? currentMarkerShape.preset === 'circle'
            ? 'transparent'
            : markerColor
          : 'none',
        border:
          currentMarkerShape?.preset === 'circle'
            ? `${dataToMarkerStyleY(
                currentMarkerShape.strokeWidth,
                fontSize
              )} solid ${markerColor}`
            : undefined,
        'border-radius':
          currentMarkerShape?.preset === 'disc' ||
          currentMarkerShape?.preset === 'circle'
            ? '50%'
            : undefined,
        'box-sizing': currentMarkerShape ? 'border-box' : undefined,
        color: currentMarkerShape ? undefined : markerColor,
        display: isOrdered ? 'inline-block' : 'block',
        'font-size': currentMarkerShape
          ? undefined
          : dataToFontSizeY(markerSize),
        height: currentMarkerShape
          ? dataToMarkerStyleY(currentMarkerShape.size, fontSize)
          : isOrdered
          ? undefined
          : '0',
        'inset-block-start': currentMarkerShape
          ? dataToMarkerStyleY(currentMarkerShape.offset, fontSize)
          : glyphBaselineOffset,
        'inset-inline-start': isOrdered
          ? '0'
          : markerHorizontalShift
          ? dataToMarkerStyleX(markerHorizontalShift, markerSize)
          : undefined,
        'letter-spacing': 'normal',
        'line-height': currentMarkerShape ? '0' : 'inherit',
        position: isOrdered ? 'absolute' : 'relative',
        'text-align': isOrdered ? 'end' : 'start',
        '-webkit-text-fill-color': currentMarkerShape ? undefined : markerColor,
        width: currentMarkerShape
          ? dataToMarkerStyleX(currentMarkerShape.size, fontSize)
          : isOrdered
          ? markerColumn
          : 'auto',
        ...(isOrdered
          ? null
          : {
              'grid-column': '1',
              'grid-row': '1',
            }),
      };

      return [
        `<li class="text-list-render-item" style="${styleAttribute(
          liStyles
        )}">`,
        `<span class="text-list-marker${
          currentMarkerShape ? ' text-list-marker-shape' : ''
        }"${
          currentMarkerShape
            ? ` data-list-marker-shape="${currentMarkerShape.preset}"`
            : ''
        } aria-hidden="true" style="${styleAttribute(markerStyles)}">${
          currentMarkerShape ? '' : escapeHtml(marker)
        }</span>`,
        `<div class="text-list-content" style="${styleAttribute({
          'grid-column': isOrdered ? undefined : '2',
          'grid-row': isOrdered ? undefined : '1',
          'min-width': '0',
        })}">`,
      ].join('');
    }
  );
}

function getMarkerFallbackWidth(glyph, markerSize) {
  const digitCount = Math.max(1, Array.from(glyph.replace(/\.$/, '')).length);
  return Math.max(markerSize, markerSize * (digitCount * 0.6 + 0.3));
}

export default getListRenderContent;
